import { memo } from "react";
import Tooltip from "ui/components/Tooltip/Tooltip";

export interface DamagePin {
  id: string;
  x: number;
  y: number;
  color: string;
  label: string;
  /** Prikazuje se u tooltipu na hover — neobavezno, stariji pozivi ih nemaju. */
  statusName?: string | null;
  description?: string | null;
}

export interface DamagePinOverlayProps {
  imageUrl: string;
  pins: DamagePin[];
  onPinClick?: (id: string) => void;
  onImageClick?: (x: number, y: number) => void;
  interactive?: boolean;
  activePin?: string | null;
}

const DamagePinOverlay: React.FC<DamagePinOverlayProps> = ({
  imageUrl,
  pins,
  onPinClick,
  onImageClick,
  interactive = false,
  activePin,
}) => {
  const handleImageClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive || !onImageClick) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    onImageClick(Math.round(x * 100) / 100, Math.round(y * 100) / 100);
  };

  return (
    <div className="relative inline-block w-full">
      <img
        src={imageUrl}
        className="w-full h-auto rounded-sm"
        alt=""
        draggable={false}
      />
      {/* Pozicija je na omotaču, a točka je okidač tooltipa — Tooltip svoj trigger
          stavlja u `relative` span, pa bi apsolutni pin unutar njega izgubio mjesto. */}
      {pins.map((pin) => (
        <div
          key={pin.id}
          className="absolute -translate-x-1/2 -translate-y-1/2 z-10"
          style={{ left: `${pin.x}%`, top: `${pin.y}%` }}
        >
          <Tooltip
            position="top-center"
            // Odabir štete u tablici odmah pokaže i njezin tooltip, bez hovera.
            open={activePin === pin.id}
            trigger={
              <div
                className={`w-5 h-5 rounded-full border-2 border-white shadow-lg cursor-pointer transition-transform ${
                  activePin === pin.id ? "scale-150" : "hover:scale-125"
                }`}
                style={{ backgroundColor: pin.color }}
                onClick={(e) => {
                  e.stopPropagation();
                  onPinClick?.(pin.id);
                }}
              />
            }
          >
            <div className="min-w-[180px] max-w-[280px]">
              {pin.statusName && (
                <div className="text-xs uppercase tracking-wide text-slate-400 mb-1 font-semibold">
                  {pin.statusName}
                </div>
              )}
              <div className="text-sm font-medium">
                {pin.label || "Bez naziva"}
              </div>
              {pin.description && (
                <div className="text-sm text-slate-300 mt-1">{pin.description}</div>
              )}
            </div>
          </Tooltip>
        </div>
      ))}
      {interactive && (
        <div
          onClick={handleImageClick}
          className="absolute inset-0 cursor-crosshair"
        />
      )}
    </div>
  );
};

export default memo(DamagePinOverlay);
